import { createContext } from 'react'

/**
 * Theme constants and the context object.
 *
 * Kept out of `ThemeProvider.jsx` so that file exports components only: a
 * module that exports both a component and a context loses fast refresh, and
 * `useTheme` would otherwise have to import the provider just to reach the
 * context.
 */

/**
 * Same key shape as `useUserPreference('theme', …, 'global')`, so the hook and
 * the provider never fight over the cache. The pre-paint script in index.html
 * reads the same key before React boots.
 */
export const THEME_STORAGE_KEY = 'pref:global:theme'

/** @type {readonly ['light', 'dark', 'system']} */
export const THEMES = ['light', 'dark', 'system']

/**
 * What a user with no stored preference gets.
 *
 * Light rather than 'system' on purpose: most of the app is still on raw
 * palette utilities that do not flip, so an OS-dark user would get light
 * panels under dark chrome. This constant — and the matching fallback in the
 * index.html pre-paint script — is the one thing to change once the sweeps
 * land.
 */
export const DEFAULT_THEME = 'light'

/**
 * `tokensVersion` increments every time the provider writes brand-derived
 * CSS custom properties to `document.documentElement`. `brand` itself can
 * already hold its final value on a consumer's very first render (e.g.
 * under `BrandingScope`, whose own context value is computed synchronously
 * from its `branding` prop) while the DOM write lands in a later commit —
 * `tokensVersion` is the thing that actually changes when the DOM catches
 * up, so anything reading a live CSS custom property should depend on it
 * (see `useCssColorVariable`).
 *
 * @type {React.Context<{theme: string, setTheme: (theme: string) => void, resolved: 'light'|'dark', brand: string, branding: Object, tokensVersion: number, setBrandPreview: (hex: string|null) => void, setBrandingPreview: (profile: Object|null) => void, setThemeOverride: (resolved: 'light'|'dark'|null) => void}|null>}
 */
export const ThemeContext = createContext(null)

/**
 * Whether a value is one of the three themes.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isTheme(value) {
  return THEMES.includes(value)
}
